import React, { useState, useEffect } from 'react';
import { Project } from '../../types/project';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Modal } from '../ui/modal';
import { cn } from '../../utils/cn';
import { useProjects } from '../../contexts/ProjectContext';
import toast from 'react-hot-toast';

interface ProjectFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: Project | null;
}

const COLOR_OPTIONS = [
  { id: 'blue', label: 'Blue', value: '#3B82F6' },
  { id: 'purple', label: 'Purple', value: '#8B5CF6' },
  { id: 'green', label: 'Green', value: '#10B981' },
  { id: 'red', label: 'Red', value: '#EF4444' },
  { id: 'orange', label: 'Orange', value: '#F59E0B' },
  { id: 'pink', label: 'Pink', value: '#EC4899' },
  { id: 'indigo', label: 'Indigo', value: '#6366F1' },
  { id: 'teal', label: 'Teal', value: '#14B8A6' },
  { id: 'yellow', label: 'Yellow', value: '#FBBF24' },
  { id: 'cyan', label: 'Cyan', value: '#06B6D4' },
  { id: 'lime', label: 'Lime', value: '#84CC16' },
  { id: 'emerald', label: 'Emerald', value: '#10B981' },
  { id: 'rose', label: 'Rose', value: '#F43F5E' },
  { id: 'sky', label: 'Sky', value: '#0EA5E9' },
  { id: 'violet', label: 'Violet', value: '#8B5CF6' },
  { id: 'fuchsia', label: 'Fuchsia', value: '#D946EF' },
];

export const ProjectForm: React.FC<ProjectFormProps> = ({
  isOpen,
  onClose,
  initialData,
}) => {
  const { addProject, updateProject } = useProjects();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(COLOR_OPTIONS[0].value);
  const [customColor, setCustomColor] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(initialData?.name || '');
      setDescription(initialData?.description || '');
      setColor(initialData?.color || COLOR_OPTIONS[0].value);
      setCustomColor('');
      setShowColorPicker(false);
    }
  }, [isOpen, initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Project name is required');
      return;
    }

    setLoading(true);
    try {
      const projectData = {
        name: name.trim(),
        description: description.trim() || undefined,
        color: customColor || color,
      };

      if (initialData) {
        await updateProject(initialData.id, projectData);
      } else {
        await addProject(projectData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Edit Project' : 'Create Project'}
      description={initialData ? 'Update your project details and settings' : 'Create a new project to organize your tasks and track progress'}
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <Input
          label="Project Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter project name"
          required
        />

        <Input
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter project description"
        />

        <div className="space-y-4">
          <label className="block text-sm font-medium text-foreground/80">
            Color
          </label>
          <div className="grid grid-cols-8 gap-2 mb-4">
            {COLOR_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  setColor(option.value);
                  setCustomColor('');
                }}
                className={cn(
                  "h-8 w-8 rounded-full border-2 transition-all duration-200",
                  (color === option.value && !customColor)
                    ? "border-primary shadow-lg scale-110" 
                    : "border-transparent hover:border-primary/30"
                )}
                style={{ backgroundColor: option.value }}
                title={option.label}
              />
            ))}
          </div>
          
          <div className="space-y-4 mt-8">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowColorPicker(!showColorPicker)}
              className="gap-2 w-full justify-between group"
            >
              <div className="flex items-center gap-2">
                <span 
                  className="w-4 h-4 rounded-full border border-border transition-transform group-hover:scale-110"
                  style={{ backgroundColor: customColor || color }} 
                />
                <span>Custom Color</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {customColor || color}
              </span>
            </Button>
            
            {showColorPicker && (
              <div className="p-6 bg-background/50 rounded-xl border-2 border-input hover:border-primary/30 transition-all">
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>Click and drag to pick a color</span>
                    <span>Current: {customColor || color}</span>
                  </div>
                  <input
                    type="color"
                    value={customColor || color}
                    onChange={(e) => {
                      setCustomColor(e.target.value);
                      setColor(e.target.value);
                    }}
                    className="w-full h-40 cursor-pointer rounded-lg"
                  />
                  <p className="text-xs text-muted-foreground">
                    Tip: Use the color picker to select any color you want. Click and drag to explore different shades.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-4 pt-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            aria-label={initialData ? 'Update Project' : 'Create Project'}
          >
            {loading ? (
              <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            ) : initialData ? (
              'Update Project'
            ) : (
              'Create Project'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};